/**
 * Contact Page — Legacy Triangle
 * Contact form with backend integration
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Phone, Mail, MapPin, Clock, Calendar, MessageSquare,
  ArrowRight, CheckCircle, ChevronDown, Send
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const CONTACT_HERO = "https://d2xsxph8kpxj0f.cloudfront.net/310519663400393649/gEFtNmSeGBDwQH79ZrNHXT/contact_hero-eMvZRPepg2ALBHgBtNWhrs.webp";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const consultationTypes = [
  { value: "strategy-call", label: "Free 30-Min Funding Strategy Call" },
  { value: "application-review", label: "Full Application Review" },
  { value: "credit-consultation", label: "Business Credit Consultation" },
  { value: "sba-loan", label: "SBA Loan Inquiry" },
  { value: "equipment-financing", label: "Equipment Financing" },
  { value: "other", label: "Other / General Inquiry" },
];

const fundingRanges = [
  { value: "under-50k", label: "Under $50,000" },
  { value: "50k-150k", label: "$50,000 – $150,000" },
  { value: "150k-500k", label: "$150,000 – $500,000" },
  { value: "500k-1m", label: "$500,000 – $1,000,000" },
  { value: "over-1m", label: "Over $1,000,000" },
  { value: "not-sure", label: "Not Sure Yet" },
];

const officeHours = [
  { day: "Monday – Friday", hours: "9:00 AM – 6:00 PM EST" },
  { day: "Saturday", hours: "10:00 AM – 2:00 PM EST" },
  { day: "Sunday", hours: "Closed" },
];

const contactCards = [
  { icon: Phone, title: "Call Us", value: "(984) 464-4672", sub: "Mon–Fri, 9AM–6PM EST", href: "tel:+19844644672", color: "#48d480" },
  { icon: Mail, title: "Email Us", value: "info@legacytriangle.com", sub: "We reply within 24 hours", href: "mailto:info@legacytriangle.com", color: "#e8821c" },
  { icon: MapPin, title: "Our Office", value: "207 W Millbrook Road, Suite 210", sub: "Raleigh, NC 27609", href: "#", color: "#48d480" },
];

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  businessName: "",
  consultationType: "",
  fundingRange: "",
  yearsInBusiness: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/contact`, form);
      setSubmitted(true);
      setForm(initialForm);
      toast.success("Message sent! We'll be in touch within 24 hours.");
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-32 md:pt-40">
        <div
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{ backgroundImage: `url(${CONTACT_HERO})`, backgroundPosition: "center top" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f2d52]/95 via-[#0f2d52]/85 to-[#0f2d52]/60" />
        <div className="container relative z-10 py-16 md:py-24">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="section-label">Get In Touch</span>
            </div>
            <h1
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-4xl md:text-6xl font-black text-white leading-tight mb-5"
            >
              Let's Talk About <br />
              <span className="text-[#48d480]">Your Funding</span>
            </h1>
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-white/80 text-lg leading-relaxed mb-8 max-w-xl"
            >
              Schedule a free consultation with one of our funding specialists. No obligation, no pressure — just honest guidance tailored to your business.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#booking-form"
                className="btn-primary flex items-center gap-2"
              >
                Book a Free Call <ArrowRight size={16} />
              </a>
              <a
                href="tel:+19844644672"
                className="btn-outline-white flex items-center gap-2"
              >
                <Phone size={16} className="text-[#48d480]" />
                (984) 464-4672
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-12 bg-gray-50 border-b border-gray-100">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {contactCards.map((card, i) => (
              <a
                key={i}
                href={card.href}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#48d480] hover:shadow-lg transition-all duration-300 group flex items-start gap-4"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${card.color}20` }}
                >
                  <card.icon size={22} style={{ color: card.color }} />
                </div>
                <div>
                  <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-bold text-sm mb-0.5">{card.title}</div>
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-[#0f2d52] font-semibold text-base group-hover:text-[#48d480] transition-colors">{card.value}</div>
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-400 text-xs mt-0.5">{card.sub}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="booking-form" className="py-20 md:py-28 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-12 items-start">

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px w-8 bg-[#48d480]" />
                <span className="section-label">Book a Consultation</span>
              </div>
              <h2
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-3xl md:text-4xl font-black text-[#0f2d52] mb-2"
              >
                Tell Us About Your Business
              </h2>
              <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 mb-8">
                Fill out the form below and a funding specialist will reach out within 24 hours.
              </p>

              {submitted ? (
                <div className="bg-[#f0fdf4] border border-[#48d480] rounded-2xl p-10 text-center">
                  <div className="w-16 h-16 bg-[#48d480] rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-white" />
                  </div>
                  <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-2xl font-black text-[#0f2d52] mb-2">
                    Message Received!
                  </h3>
                  <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-600 mb-6">
                    Thank you for reaching out. One of our funding specialists will contact you within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-[#48d480] font-semibold hover:underline"
                  >
                    Submit another inquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  {/* Name Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">First Name *</label>
                      <input type="text" name="firstName" value={form.firstName} onChange={handleChange} required placeholder="John" className="form-input" data-testid="first-name-input" />
                    </div>
                    <div>
                      <label className="form-label">Last Name *</label>
                      <input type="text" name="lastName" value={form.lastName} onChange={handleChange} required placeholder="Smith" className="form-input" data-testid="last-name-input" />
                    </div>
                  </div>

                  {/* Contact Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Email Address *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="john@yourbusiness.com" className="form-input" data-testid="email-input" />
                    </div>
                    <div>
                      <label className="form-label">Phone Number *</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder="(555) 000-0000" className="form-input" data-testid="phone-input" />
                    </div>
                  </div>

                  {/* Business Name */}
                  <div>
                    <label className="form-label">Business Name *</label>
                    <input type="text" name="businessName" value={form.businessName} onChange={handleChange} required placeholder="Your Business LLC" className="form-input" data-testid="business-name-input" />
                  </div>

                  {/* Consultation Type */}
                  <div>
                    <label className="form-label">Type of Consultation *</label>
                    <div className="relative">
                      <select name="consultationType" value={form.consultationType} onChange={handleChange} required className="form-input appearance-none pr-10" data-testid="consultation-type-select">
                        <option value="">Select a consultation type...</option>
                        {consultationTypes.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Funding + Years */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Funding Amount Needed</label>
                      <div className="relative">
                        <select name="fundingRange" value={form.fundingRange} onChange={handleChange} className="form-input appearance-none pr-10">
                          <option value="">Select a range...</option>
                          {fundingRanges.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                    <div>
                      <label className="form-label">Years in Business</label>
                      <div className="relative">
                        <select name="yearsInBusiness" value={form.yearsInBusiness} onChange={handleChange} className="form-input appearance-none pr-10">
                          <option value="">Select...</option>
                          <option value="startup">Startup (Less than 1 year)</option>
                          <option value="1-2">1–2 Years</option>
                          <option value="2-5">2–5 Years</option>
                          <option value="5-10">5–10 Years</option>
                          <option value="10+">10+ Years</option>
                        </select>
                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="form-label">Tell Us About Your Needs</label>
                    <textarea name="message" value={form.message} onChange={handleChange} rows={4} placeholder="Briefly describe your business and what you're looking to fund..." className="form-input resize-none" data-testid="message-textarea" />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="w-full bg-[#48d480] text-[#0f2d52] font-bold py-4 rounded-xl flex items-center justify-center gap-2 hover:bg-[#2fb866] transition-all disabled:opacity-60"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-[#0f2d52]/30 border-t-[#0f2d52] rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Book My Free Consultation
                      </>
                    )}
                  </button>

                  <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-center text-gray-400 text-xs">
                    By submitting this form, you agree to our Privacy Policy. We never share your information.
                  </p>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Why Book */}
              <div className="bg-[#0f2d52] rounded-2xl p-7 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 opacity-10 pointer-events-none">
                  <svg width="120" height="120" viewBox="0 0 200 200">
                    <polygon points="100,10 190,180 10,180" stroke="white" strokeWidth="3" fill="none" />
                  </svg>
                </div>
                <div className="relative z-10">
                  <div className="w-10 h-10 bg-[#48d480] rounded-lg flex items-center justify-center mb-4">
                    <MessageSquare size={20} className="text-[#0f2d52]" />
                  </div>
                  <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="font-black text-lg mb-4">Why Book a Free Call?</h3>
                  <div className="space-y-3">
                    {["Get a personalized funding roadmap", "Understand your eligibility in minutes", "No obligation, no pressure", "Expert guidance from day one", "Learn about options banks won't tell you"].map((item, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <CheckCircle size={15} className="text-[#48d480] flex-shrink-0 mt-0.5" />
                        <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/80 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Office Hours */}
              <div className="bg-white rounded-2xl p-7 border border-gray-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-[#f0f7f4] rounded-lg flex items-center justify-center">
                    <Clock size={20} className="text-[#48d480]" />
                  </div>
                  <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-bold">Office Hours</h3>
                </div>
                <div className="space-y-3">
                  {officeHours.map((h, i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                      <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-600 text-sm font-medium">{h.day}</span>
                      <span style={{ fontFamily: "'Inter', sans-serif" }} className={`text-sm font-semibold ${h.hours === "Closed" ? "text-red-400" : "text-[#48d480]"}`}>{h.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Response Time */}
              <div className="bg-[#f0f7f4] rounded-2xl p-7 border border-[#48d480]/20">
                <div className="text-center">
                  <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-4xl font-black text-[#0f2d52] mb-1">&lt; 24hrs</div>
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm font-medium">Average response time</div>
                  <div className="h-px bg-gray-200 my-4" />
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm">Prefer to talk now? Call us directly at</div>
                  <a href="tel:+18005551234" style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-black text-lg hover:text-[#48d480] transition-colors">(800) 555-1234</a>
                </div>
              </div>

              <Link to="/" className="flex items-center justify-center gap-2 text-gray-400 hover:text-[#0f2d52] text-sm transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
